import { Alert, Avatar, Box, Stack, Typography } from "@mui/material";
import type { ReactNode } from "react";

import { Assistant } from "@mui/icons-material";
import { getColorFromPersona } from "src/reusable/Functions";
import AAChatInputButtons from "src/views/assistant/components/ChatInput/AAChatInputButtons";
import { getErrorMessage } from "src/views/remote-resources/modals/CreateResourceModal";
import { useActivePersonaIdentity, useChatStore } from "../../stores/ChatStore";
import { type SendMessageOptions } from "src/reusable/Chat/ChatStreaming/stores/CoreStreamingSlice";
import { ImageContent } from "../../types";
import AtomAssistChatInput from "../AtomAssistChatInput";
import AAChatInputBar from "../ChatInput/AAChatInputBar";
import InitialChatPrompt from "../InitialChatPrompt";
import RecentChats from "./RecentChats";

type AASplashScreenProps = {
  chatStreamError: string;
  isChatConnectionError: boolean;
  isChatConnected: boolean;
  isChatLoading: boolean;
  isChatEnded: boolean;
  terminateConnection: () => void;
  /** Send message with optional VLM images */
  sendUserMessage: (
    text: string,
    images?: ImageContent[],
    opts?: SendMessageOptions,
  ) => Promise<boolean>;
  /** Shown above the heading, e.g. a link's agent was not found. */
  notice?: ReactNode;
};

const AASplashScreen = ({
  chatStreamError,
  isChatConnectionError,
  isChatConnected,
  isChatLoading,
  isChatEnded,
  terminateConnection,
  sendUserMessage,
  notice,
}: AASplashScreenProps) => {
  const { persona } = useChatStore();
  const identity = useActivePersonaIdentity();
  const personaName = identity.name;
  const color = getColorFromPersona(personaName);

  return (
    <Box
      data-testid="aa-splash-container"
      data-attr="assistant:form:splash-screen"
      sx={{
        minHeight: "calc(100vh - 120px)", // Account for header/footer
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        p: 1,
      }}
    >
      {notice && <Box sx={{ maxWidth: "830px", width: "100%" }}>{notice}</Box>}
      <Box
        sx={{
          textAlign: "center",
          justifyContent: "center",
          alignSelf: "center",
          width: "100%",
        }}
      >
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          justifyContent="center"
          sx={{ width: "100%", mb: 3 }}
          flexWrap="wrap"
        >
          {personaName ? (
            <Avatar sx={{ bgcolor: color, width: 40, height: 40 }}>
              <Assistant />
            </Avatar>
          ) : (
            <img
              src="/images/nuclearn-n-full.png"
              alt="Nuclearn Logo"
              width={40}
              height={40}
            />
          )}
          <Typography sx={{ fontFamily: "Poppins", fontSize: "36px" }}>
            {personaName ? personaName : "How can I help?"}
          </Typography>
        </Stack>
      </Box>

      {persona && (
        <Box sx={{ maxWidth: "830px", width: "100%", mb: 2 }}>
          <InitialChatPrompt />
        </Box>
      )}

      {isChatConnectionError && (
        <Box sx={{ width: 700 }}>
          <Alert severity="error" sx={{ mb: 2, width: "100%" }}>
            <Typography variant="body2">
              {getErrorMessage(chatStreamError) ||
                "An error occurred while connecting to the chat."}
            </Typography>
          </Alert>
        </Box>
      )}
      <AtomAssistChatInput
        isChatConnected={isChatConnected}
        isChatLoading={isChatLoading}
        isChatEnded={isChatEnded}
        isSplashScreen={true}
        terminateConnection={terminateConnection}
        sendUserMessage={sendUserMessage}
        InputButtons={AAChatInputButtons}
        InputBar={AAChatInputBar}
      />

      <Box
        mt={2}
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <RecentChats productKey="atom-assist" />
      </Box>
    </Box>
  );
};

export default AASplashScreen;
